function parseJsonField(value) {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return null;
}

function firstDefined(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
}

function parseDescriptionMeta(description) {
  if (!description || typeof description !== 'string') {
    return { serviceType: null, commentaires: null, colis: null };
  }
  const serviceMatch = description.match(/\[service:\s*([^\]]+)\]/i);
  const commentMatch = description.match(/Commentaires:\s*(.+?)(?:\n|$)/i);
  const colisMatch = description.match(/Colis:\s*(.+?)(?:\n|$)/i);
  let colis = null;
  if (colisMatch) {
    try {
      colis = JSON.parse(colisMatch[1].trim());
    } catch {
      colis = colisMatch[1].trim();
    }
  }
  return {
    serviceType: serviceMatch ? serviceMatch[1].trim() : null,
    commentaires: commentMatch ? commentMatch[1].trim() : null,
    colis,
  };
}

const HOUSING_LABELS = {
  maison: 'Maison',
  appartement: 'Appartement',
  bureau: 'Bureau',
  studio: 'Studio',
  local: 'Local',
};

const SERVICE_TYPE_LABELS = {
  demenagement: 'Déménagement',
  transport: 'Transport',
  transport_international: 'Transport international',
};

const TIME_SLOT_LABELS = {
  morning: 'Matin',
  afternoon: 'Après-midi',
};

const ADDITIONAL_SERVICE_LABELS = {
  montage_demontage: 'Montage/Démontage',
  emballage: 'Emballage professionnel',
  garde_meuble: 'Garde-meuble',
  carton: 'Fourniture de cartons',
};

function labelize(map, value) {
  if (value == null || value === '') return null;
  const key = String(value).toLowerCase();
  return map[key] || String(value);
}

function formatBool(value) {
  if (value === true) return 'Oui';
  if (value === false) return 'Non';
  return null;
}

function summarizeCuisine(cuisine) {
  if (!cuisine || typeof cuisine !== 'object') return null;
  const parts = [];
  if (cuisine.petit) parts.push(`Petit: ${cuisine.petit}`);
  if (cuisine.moyen) parts.push(`Moyen: ${cuisine.moyen}`);
  if (cuisine.grande) parts.push(`Grand: ${cuisine.grande}`);
  return parts.length ? parts.join(' · ') : null;
}

function summarizeChambres(chambres) {
  if (!Array.isArray(chambres) || chambres.length === 0) return null;
  return chambres.map((chambre, index) => {
    const name = chambre?.nom || `Chambre ${index + 1}`;
    const articles = chambre?.articles;
    if (!articles || typeof articles !== 'object') return name;
    const counts = [];
    if (articles.petit && articles.petit !== '0') counts.push(`petit ${articles.petit}`);
    if (articles.moyen && articles.moyen !== '0') counts.push(`moyen ${articles.moyen}`);
    if (articles.grande && articles.grande !== '0') counts.push(`grand ${articles.grande}`);
    return counts.length ? `${name} (${counts.join(', ')})` : name;
  });
}

export function parseMissionFormData(quote) {
  if (!quote) return { fields: [], hasData: false };

  const services = parseJsonField(quote.services) || {};
  const nested = services.serviceDetails && typeof services.serviceDetails === 'object'
    ? services.serviceDetails
    : {};
  const descMeta = parseDescriptionMeta(quote.description);

  const serviceTypeRaw = firstDefined(
    services.serviceType,
    services.service_type,
    nested.serviceType,
    descMeta.serviceType
  );

  const propertyTypeRaw = firstDefined(
    quote.housing_type,
    services.propertyType,
    nested.propertyType,
    services.logement
  );

  const floorRaw = firstDefined(
    quote.floor,
    services.floor,
    nested.floor,
    services.etage
  );

  const elevatorRaw = firstDefined(
    quote.has_elevator,
    services.hasElevator,
    nested.hasElevator,
    services.ascenseur
  );

  const timeSlotRaw = firstDefined(services.time_slot, services.timeSlot, nested.timeSlot);
  const additionalServicesRaw = firstDefined(
    services.additional_services,
    services.additionalServices,
    nested.additionalServices
  );

  const fields = [
    { key: 'serviceType', label: 'Type de mission', value: labelize(SERVICE_TYPE_LABELS, serviceTypeRaw) },
    { key: 'propertyType', label: 'Type de logement', value: labelize(HOUSING_LABELS, propertyTypeRaw) },
    {
      key: 'unitNumber',
      label: 'N° appartement / bureau',
      value: firstDefined(services.unit_number, services.unitNumber, nested.unit_number),
    },
    {
      key: 'floor',
      label: 'Étage',
      value: floorRaw === 0 || floorRaw === '0' ? 'Rez-de-chaussée' : (floorRaw != null ? String(floorRaw) : null),
    },
    {
      key: 'hasElevator',
      label: 'Ascenseur',
      value:
        formatBool(
          typeof elevatorRaw === 'string'
            ? ['true', '1', 'oui', 'yes'].includes(elevatorRaw.toLowerCase())
            : elevatorRaw
        ) ?? (elevatorRaw === false ? 'Non' : null),
    },
    {
      key: 'volumeM3',
      label: 'Volume estimé (m³)',
      value: firstDefined(quote.volume_m3, services.volume_m3, services.volume),
    },
    {
      key: 'truckType',
      label: 'Type de véhicule / camion',
      value: firstDefined(quote.truck_type, services.truck_type, services.vehicleType),
    },
    { key: 'timeSlot', label: 'Créneau horaire', value: labelize(TIME_SLOT_LABELS, timeSlotRaw) },
    { key: 'article', label: 'Article / objet', value: firstDefined(services.article, nested.article) },
    {
      key: 'cuisine',
      label: 'Cuisine (volume)',
      value: summarizeCuisine(firstDefined(nested.cuisine, services.cuisine)),
    },
    {
      key: 'chambres',
      label: 'Chambres',
      value: (() => {
        const list = summarizeChambres(firstDefined(nested.chambres, services.chambres));
        return list ? list.join(' | ') : null;
      })(),
    },
    {
      key: 'additionalServices',
      label: 'Services supplémentaires',
      value: (() => {
        const raw = additionalServicesRaw;
        if (!raw) return null;
        const list = Array.isArray(raw) ? raw : [raw];
        const labels = list.map((item) => {
          const key = String(item).toLowerCase();
          return ADDITIONAL_SERVICE_LABELS[key] || String(item);
        });
        return labels.length ? labels.join(', ') : null;
      })(),
    },
    {
      key: 'notes',
      label: 'Notes du client',
      value: firstDefined(nested.notes, services.notes, descMeta.commentaires),
    },
    {
      key: 'packages',
      label: 'Colis / packages',
      value: (() => {
        const colis = firstDefined(services.packages, services.colis, descMeta.colis);
        if (colis == null) return null;
        return typeof colis === 'object' ? JSON.stringify(colis) : String(colis);
      })(),
    },
    { key: 'manutention', label: 'Manutention', value: firstDefined(services.manutention) },
    {
      key: 'baggageWeightKg',
      label: 'Poids bagages (kg)',
      value: firstDefined(services.baggageWeightKg, services.baggage_weight_kg),
    },
    {
      key: 'pickupType',
      label: 'Type de retrait',
      value: firstDefined(services.pickupType, services.pickup_type),
    },
    {
      key: 'isQuickService',
      label: 'Service rapide',
      value: services.isQuickService === true ? 'Oui' : (services.isQuickService === false ? 'Non' : null),
    },
    { key: 'description', label: 'Message / description', value: quote.description || null },
  ].map((field) => ({
    ...field,
    display: field.value != null && String(field.value).trim() !== '' ? String(field.value) : null,
  }));

  return {
    fields,
    hasData: fields.some((f) => f.display),
  };
}
